import { useModal } from "@ebay/nice-modal-react";
import { useEffect, useRef, useState } from "react";
import { useRecoilValue } from "recoil";

import SimpleModal from "components/modals/SimpleModal";
import { tokenState } from "store/store";
import { useLogout } from "./apis/auth";

const TOTAL_TIME = 30 * 60 * 1000;
const POPUP_TIME = 10 * 60 * 1000;
const SYNC_INTERVAL = 1000;

const useInactivityLogout = () => {
  const logoutMutation = useLogout();
  const token = useRecoilValue(tokenState);
  const modal = useModal(SimpleModal);

  const [timeRemaining, setTimeRemaining] = useState(TOTAL_TIME);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const resetTimer = () => {
    setTimeRemaining(TOTAL_TIME);
    localStorage.setItem("timerStart", Date.now().toString());
    modal.hide();
  };

  const handleLogout = () => {
    modal.hide();
    logoutMutation.mutate();
    clearInterval(intervalRef.current!);
    setTimeRemaining(0);
    localStorage.removeItem("timerStart");
  };

  const syncTimer = () => {
    const timerStart = localStorage.getItem("timerStart");
    if (timerStart) {
      const elapsed = Date.now() - parseInt(timerStart, 10);
      const remaining = TOTAL_TIME - elapsed;
      setTimeRemaining(remaining > 0 ? remaining : 0);

      if (remaining <= 0) {
        handleLogout();
      } else if (remaining <= POPUP_TIME) {
        if (!modal.visible) {
          showPopup();
        }
      } else {
        if (modal.visible) {
          modal.hide();
        }
      }
    }
  };

  const showPopup = () => {
    modal
      .show({
        showCancel: false,
        submitText: "세션 연장",
        message: "사용하지 않아 곧 로그아웃됩니다.",
      })
      .then(() => {
        resetTimer();
      })
      .catch(() => {
        handleLogout();
      });
  };

  useEffect(() => {
    if (token) {
      localStorage.setItem("timerStart", Date.now().toString());

      intervalRef.current = setInterval(() => {
        syncTimer();
      }, SYNC_INTERVAL);

      return () => {
        clearInterval(intervalRef.current!);
        localStorage.removeItem("timerStart");
      };
    }
  }, [token]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "timerStart") {
        syncTimer();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  return {
    timeRemaining: Math.max(0, timeRemaining),
    resetTimer,
  };
};

export default useInactivityLogout;
